import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import React, { useRef, useState } from 'react';
import styles from './zip-placement-picker.module.scss';

export type SignaturePlacement = {
	/** 0–1 fraction of page width */
	xPct: number;
	/** 0–1 fraction of page height */
	yPct: number;
	pageNumber: number;
};

type ZipPlacementPickerProps = {
	zipFileName: string;
	onConfirm: (placement?: SignaturePlacement) => void;
	onBack: () => void;
};

// Signature ghost dimensions as a fraction of canvas
const SIG_WIDTH_PCT = 0.28;
const SIG_HEIGHT_PCT = 0.07;

export default function ZipPlacementPicker(props: ZipPlacementPickerProps) {
	const [placement, setPlacement] = useState<{
		xPct: number;
		yPct: number;
	} | null>(null);
	const [pageNumber, setPageNumber] = useState(1);
	const canvasRef = useRef<HTMLDivElement | null>(null);

	const handleCanvasClick = (e: React.MouseEvent<HTMLDivElement>) => {
		const rect = canvasRef.current?.getBoundingClientRect();
		if (!rect) return;

		const rawXPct = (e.clientX - rect.left) / rect.width;
		const rawYPct = (e.clientY - rect.top) / rect.height;

		// Clamp so the ghost box stays within bounds
		const xPct = Math.min(
			Math.max(rawXPct - SIG_WIDTH_PCT / 2, 0),
			1 - SIG_WIDTH_PCT,
		);
		const yPct = Math.min(
			Math.max(rawYPct - SIG_HEIGHT_PCT / 2, 0),
			1 - SIG_HEIGHT_PCT,
		);

		setPlacement({ xPct, yPct });
	};

	const handlePageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const value = parseInt(e.target.value, 10);
		if (!isNaN(value) && value >= 1) setPageNumber(value);
	};

	const handleConfirm = () => {
		props.onConfirm(
			placement
				? {
					xPct: placement.xPct,
					yPct: placement.yPct,
					pageNumber,
				}
				: undefined,
		);
	};

	return (
		<div className={styles['placement-picker']}>
			<h2 className={styles['title']}>Posiciona la firma</h2>
			<p className={styles['subtitle']}>
				Haz clic en la página para indicar dónde quieres colocar la
				firma. Se aplicará a todos los PDFs dentro de{' '}
				<strong>{props.zipFileName}</strong>.
			</p>

			<div className={styles['page-input-row']}>
				<Label htmlFor="page-number">Aplicar en la página</Label>
				<Input
					id="page-number"
					type="number"
					min={1}
					value={pageNumber}
					onChange={handlePageChange}
					className={styles['page-input']}
				/>
			</div>

			<div
				ref={canvasRef}
				className={styles['page-canvas']}
				onClick={handleCanvasClick}
				role="button"
				tabIndex={0}
				aria-label="Haz clic para posicionar la firma"
				onKeyDown={(e) => {
					if (e.key === 'Enter' || e.key === ' ')
						handleCanvasClick(e as never);
				}}
			>
				<span className={styles['page-hint']}>
					{placement ? '' : 'Haz clic aquí para posicionar la firma'}
				</span>

				{placement && (
					<div
						className={styles['signature-ghost']}
						style={{
							left: `${placement.xPct * 100}%`,
							top: `${placement.yPct * 100}%`,
							width: `${SIG_WIDTH_PCT * 100}%`,
							height: `${SIG_HEIGHT_PCT * 100}%`,
						}}
					>
						<span className={styles['ghost-label']}>Firma</span>
					</div>
				)}
			</div>

			<div className={styles['actions']}>
				<Button variant="secondary" onClick={props.onBack}>
					Atrás
				</Button>
				<Button onClick={handleConfirm}>Continuar</Button>
			</div>
		</div>
	);
}
